import { getFeaturePanelDefinition } from "../feature-panel-registry.ts";
import type { RuntimeFeatureModule } from "../runtime-feature-registry.ts";
import {
  applyColorEffectValue,
  resetColorEffectValue,
} from "../shared/page-color-effects.ts";
import {
  createRangeControl,
  formatNumber,
  normalizeSteppedValue,
  parseNumericConfig,
} from "../shared/feature-value.ts";

const variable = "--migrosone-a11y-blue-filter";
const attribute = "data-migrosone-a11y-blue-filter";
const fallback = {
  defaultValue: 0,
  min: 0,
  max: 60,
  step: 10,
};

function getConfig(config: unknown) {
  return parseNumericConfig({
    config,
    defaultKeys: ["intensity", "scale"],
    fallback,
  });
}

function createFilterValue(intensity: number) {
  if (intensity <= 0) {
    return "";
  }

  const saturation = Math.max(40, 100 - intensity * 0.75);
  return `sepia(${formatNumber(intensity)}%) saturate(${formatNumber(saturation)}%)`;
}

const definition = getFeaturePanelDefinition("blue-filter");

if (!definition) {
  throw new Error("Missing blue-filter feature definition");
}

export const blueFilterFeatureModule: RuntimeFeatureModule = {
  key: "blue-filter",
  definition,
  isSupported({ document }) {
    return Boolean(document.head && document.body && document.documentElement);
  },
  renderControl({ config, document, onPreferenceChange, preference }) {
    return createRangeControl({
      config: getConfig(config),
      document,
      label: "Mavi filtre",
      onPreferenceChange,
      preference,
      suffix: "%",
    });
  },
  apply({ config, document, preference }) {
    const numericConfig = getConfig(config);
    const value = normalizeSteppedValue(preference, numericConfig);
    const filterValue = createFilterValue(value);
    if (!filterValue) {
      resetColorEffectValue({ attribute, document, variable });
      return;
    }

    applyColorEffectValue({
      attribute,
      document,
      value: filterValue,
      variable,
    });
  },
  reset({ config, document }) {
    resetColorEffectValue({ attribute, document, variable });
    const numericConfig = getConfig(config);
    const filterValue = createFilterValue(numericConfig.defaultValue);
    if (filterValue) {
      applyColorEffectValue({
        attribute,
        document,
        value: filterValue,
        variable,
      });
    }
  },
};
